import Link from 'next/link';
import type { FC } from 'react';

import Text from '@/components/Text';
import { cn } from '@/lib/utils';
import type { ExternalOffer } from '@/services/external-offers';
import { timeAgo } from '@/utils/date';

import CompanyImage from '../CompanyImage';
import { getWorkTypeLabel } from '../Offer';
import OfferLocation from '../OfferLocation';
import OfferSalary from '../OfferSalary';

type Props = ExternalOffer & { isCurrent?: boolean };

const OfferListItem: FC<Props> = ({
  id,
  jobTitle,
  companyName,
  companyImage,
  locations,
  salaryLower,
  salaryUpper,
  salaryPeriod,
  salaryCurrency,
  createdAt,
  isHybrid,
  isOnsite,
  isRemote,
  isCurrent = false,
}) => (
  <Link
    href={{ pathname: '/jobs/[id]', query: { id: id.toString() } }}
    aria-current={isCurrent ? 'page' : undefined}
    className={cn(
      'flex flex-col gap-3 border-b border-jb-neutral-400 p-4 transition-colors hover:bg-jb-neutral-200',
      {
        'rounded-2xl border-b-0 bg-jb-primary-100 p-5 hover:bg-jb-primary-100':
          isCurrent,
      }
    )}
  >
    <div className="flex w-full min-w-0 flex-row gap-2">
      <CompanyImage
        src={companyImage?.url}
        placeholderIconProps={{ fontSize: 28 }}
        imageContainerClassName="h-9 w-9"
        height={companyImage?.height ?? 0}
        width={companyImage?.width ?? 0}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Text
          variant="jb-body-large"
          title={jobTitle}
          className="truncate font-bold text-jb-neutral-1000"
        >
          {jobTitle}
        </Text>
        <Text
          variant="jb-body-medium"
          title={companyName}
          className="truncate text-jb-neutral-800"
        >
          {companyName}
        </Text>
      </div>
    </div>
    <div className="flex w-full items-end justify-between gap-2">
      <div className="flex min-w-0 flex-col items-start gap-1">
        <OfferLocation
          locations={locations}
          workType={getWorkTypeLabel({ isHybrid, isOnsite, isRemote })}
        />
        <OfferSalary
          format="k"
          salaryLower={salaryLower}
          salaryUpper={salaryUpper}
          salaryPeriod={salaryPeriod}
          currency={salaryCurrency}
        />
      </div>
      <Text variant="jb-body-small" className="shrink-0 text-jb-neutral-700">
        {timeAgo(new Date(createdAt))}
      </Text>
    </div>
  </Link>
);

export default OfferListItem;
